import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import axiosInstance from "../../utils/axiosInstance";

const AirbnbAmenities = ({ airbnbId }) => {
  const [allAmenities, setAllAmenities] = useState([]);
  const [assignedAmenities, setAssignedAmenities] = useState([]);
  const [selectedAmenity, setSelectedAmenity] = useState("");

  // Fetch all amenities
  const fetchAllAmenities = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/airbnbs/amenities"); // global list
      setAllAmenities(res.data);
    } catch (err) {
      console.error("❌ Failed to fetch amenities:", err);
    }
  }, []);

  // Fetch assigned amenities for this Airbnb
  const fetchAssignedAmenities = useCallback(async () => {
    if (!airbnbId) return;
    try {
      const res = await axiosInstance.get(`/airbnbs/${airbnbId}/amenities`); // assigned amenities
      setAssignedAmenities(res.data);
    } catch (err) {
      console.error("❌ Failed to fetch assigned amenities:", err);
    }
  }, [airbnbId]);

  useEffect(() => {
    fetchAllAmenities();
    fetchAssignedAmenities();
  }, [fetchAllAmenities, fetchAssignedAmenities]);

  const handleAssignAmenity = async () => {
    if (!selectedAmenity) return;
    try {
      await axiosInstance.post(`/airbnbs/${airbnbId}/amenities`, {
        amenity_id: Number(selectedAmenity), // send numeric ID
      });
      setSelectedAmenity("");
      fetchAssignedAmenities();
    } catch (err) {
      console.error("❌ Failed to assign amenity:", err);
    }
  };

  const handleRemoveAmenity = async (amenityId) => {
    if (window.confirm("Remove this amenity?")) {
      try {
        await axiosInstance.delete(`/airbnbs/${airbnbId}/amenities/${amenityId}`);
        fetchAssignedAmenities();
      } catch (err) {
        console.error("❌ Failed to remove amenity:", err);
      }
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6">Manage Amenities</Typography>

      {/* Assign New Amenity */}
      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        <Select
          value={selectedAmenity}
          onChange={(e) => setSelectedAmenity(e.target.value)}
          displayEmpty
          fullWidth
        >
          <MenuItem value="">Select Amenity</MenuItem>
          {allAmenities.map((a) => {
            const isAssigned = assignedAmenities.some(
              (aa) => Number(aa.id) === Number(a.id)
            );
            return (
              <MenuItem key={a.id} value={a.id} disabled={isAssigned}>
                {a.name} {isAssigned ? "(assigned)" : ""}
              </MenuItem>
            );
          })}
        </Select>
        <Button variant="contained" onClick={handleAssignAmenity}>
          Assign
        </Button>
      </Box>

      {/* List Assigned Amenities */}
      <Box sx={{ mt: 2 }}>
        {assignedAmenities.length > 0 ? (
          assignedAmenities.map((a) => (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1.5,
                mb: 1,
                border: "1px solid #ccc",
                borderRadius: 2,
              }}
            >
              <Typography>{a.name}</Typography>
              <IconButton onClick={() => handleRemoveAmenity(a.id)}>
                <DeleteIcon color="error" />
              </IconButton>
            </Box>
          ))
        ) : (
          <Typography>No amenities assigned.</Typography>
        )}
      </Box>
    </Box>
  );
};

export default AirbnbAmenities;
